/**
 * Crew insurance and equipment in the portal:
 *  - InsurancePanel and InsuranceBlock on Crew & tickets (certificates per crew member; the rostering block)
 *  - InsuranceOverride, shown when rostering someone without current insurance is refused
 *  - Equipment (#/equipment): kits and their items
 *  - ShiftKits on each shift in Jobs (assign kits, see the before and after checks)
 *  - PackKitChecks in the job pack (before and after checks with photos)
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { Icon } from "../../components/Icon";
import { Notice, useNotice } from "../../components/Notice";
import { PageHeader } from "../../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton } from "../../components/States";
import { BRAND } from "../../lib/config";
import { fmtDate, fmtPlainDate, localYmd } from "../../lib/format";
import { openBlobInNewTab } from "../../lib/jobs-data";
import { useAsync } from "../../lib/useAsync";
import {
  addKitItem,
  assignKit,
  blockSetting,
  certificateFile,
  certificates,
  checkPhoto,
  kits,
  packKits,
  removeCertificate,
  removeKitItem,
  saveKit,
  shiftKits,
  setBlock,
  submitCheck,
  unassignKit,
  uploadCertificate,
  type Certificate,
  type CheckRow,
  type Kit,
  type ShiftKitRow,
} from "./records-data";

export const insuranceInUse = () => Boolean(BRAND.crewInsurance);
export const equipmentInUse = () => Boolean(BRAND.equipment);
const money = (n: number) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(n);

function Field({ label, hint, wide, children }: { label: string; hint?: string; wide?: boolean; children: ReactNode }) {
  return (
    <label className={`field${wide ? " field--wide" : ""}`}>
      <span className="field__label">{label}</span>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </label>
  );
}

/* ------------------------------------------------------------------ insurance */

/** A crew member's insurance status for lists: current, expiring, expired or none. */
export function insuranceChip(expiry: string | null, partner: boolean) {
  if (partner) return <Chip tone="grey">Partner</Chip>;
  if (!expiry) return <Chip tone="primary-outline">No insurance</Chip>;
  const today = localYmd();
  if (expiry < today) return <Chip tone="primary">Insurance expired</Chip>;
  const soon = new Date(`${today}T12:00:00Z`);
  soon.setUTCDate(soon.getUTCDate() + 30);
  return expiry <= soon.toISOString().slice(0, 10) ? <Chip tone="accent">Expires {fmtPlainDate(expiry)}</Chip> : <Chip tone="green">Insured</Chip>;
}

export function InsuranceBlock() {
  const s = useAsync(blockSetting);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  if (!insuranceInUse() || !s.data) return null;
  const { block, director } = s.data;
  return (
    <div className="card">
      <div className="card__body stack">
        <Notice notice={notice} onDismiss={() => setNotice(null)} />
        <p className="small" style={{ margin: 0 }}>
          <strong>Insurance on shifts:</strong> {block ? "crew without a current public liability certificate can't be put on a shift unless you record a reason." : "not checked yet. Turn it on once certificates are uploaded."} Partners are never checked.
        </p>
        {director && (
          <div className="form-actions">
            <button type="button" className={`btn btn--sm ${block ? "btn--outline" : "btn--ink"}`} disabled={busy} onClick={() => { setBusy(true); setNotice(null); setBlock(!block).then(() => { ok(block ? "Insurance is no longer checked on shifts." : "Insurance is now checked on shifts."); s.reload(); }).catch(fail).finally(() => setBusy(false)); }}>
              {block ? "Stop checking insurance on shifts" : "Check insurance on shifts"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function InsurancePanel({ crewId, onChanged }: { crewId: string; onChanged?: () => void }) {
  const s = useAsync(certificates);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const mine = (s.data ?? []).filter((c) => c.crew_member_id === crewId);
  const min = BRAND.crewInsurance?.minimumCover ?? 0;
  async function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const file = f.get("file");
    if (!(file instanceof File) || !file.size) return;
    setBusy(true);
    setNotice(null);
    try {
      await uploadCertificate(crewId, { insurer: String(f.get("insurer")), policyNumber: String(f.get("policyNumber") ?? ""), coverAmount: String(f.get("coverAmount")), expiresOn: String(f.get("expiresOn")), file });
      ok("Certificate added.");
      setAdding(false);
      s.reload();
      onChanged?.();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack" style={{ gap: 8 }}>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <p className="small strong" style={{ margin: 0 }}>Public liability insurance</p>
      {s.error && <p className="small sub--primary">{s.error}</p>}
      {s.data && !mine.length && <p className="small muted" style={{ margin: 0 }}>No certificate on file.</p>}
      {mine.map((c: Certificate) => (
        <div key={c.id} className="small">
          <button type="button" className="linkbtn strong" onClick={() => void openBlobInNewTab(() => certificateFile(c.id))}>{c.insurer}</button>
          {c.policy_number ? `, ${c.policy_number}` : ""}, {money(c.cover_amount)}, expires {fmtPlainDate(c.expires_on)}
          {c.expires_on < localYmd() && <span className="sub sub--primary">Expired</span>}{" "}
          <button type="button" className="linkbtn" disabled={busy} onClick={() => { setBusy(true); removeCertificate(c.id).then(() => { ok("Certificate removed."); s.reload(); onChanged?.(); }).catch(fail).finally(() => setBusy(false)); }}>Remove</button>
        </div>
      ))}
      {!adding && <div><button type="button" className="btn btn--outline btn--xs" onClick={() => setAdding(true)}><Icon name="plus" size={14} /> Add certificate</button></div>}
      {adding && (
        <form className="form-grid" onSubmit={(e) => void add(e)}>
          <Field label="Insurer"><input className="input" name="insurer" required maxLength={200} /></Field>
          <Field label="Policy number"><input className="input" name="policyNumber" maxLength={80} /></Field>
          <Field label="Cover (dollars)" hint={`At least ${money(min)}`}><input className="input" name="coverAmount" type="number" min={min} step={1} required /></Field>
          <Field label="Expires"><input className="input" name="expiresOn" type="date" min={localYmd()} required /></Field>
          <Field label="Certificate" hint="PDF, JPEG or PNG" wide><input className="input" name="file" type="file" required accept="application/pdf,image/jpeg,image/png" /></Field>
          <div className="form-actions field--wide"><button type="submit" className="btn btn--ink btn--sm" disabled={busy} aria-busy={busy}>Upload</button><button type="button" className="btn btn--outline btn--sm" onClick={() => setAdding(false)}>Cancel</button></div>
        </form>
      )}
    </div>
  );
}

/** Rostering someone without current insurance: record why, or cancel. */
export function InsuranceOverride({ name, busy, onConfirm, onCancel }: { name: string; busy: boolean; onConfirm: (reason: string) => void; onCancel: () => void }) {
  const [reason, setReason] = useState("");
  return (
    <div className="card card--alert" role="alertdialog" aria-label="No current insurance">
      <div className="card__body stack" style={{ gap: 8 }}>
        <p className="small" style={{ margin: 0 }}><strong>{name}</strong> has no current public liability insurance on file for this shift's date. Upload their certificate on Crew & tickets, or put them on with a reason. The reason is recorded with your name.</p>
        <Field label="Reason"><input className="input" value={reason} minLength={5} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Certificate emailed, uploading tonight" /></Field>
        <div className="form-actions"><button type="button" className="btn btn--primary btn--xs" disabled={busy || reason.trim().length < 5} onClick={() => onConfirm(reason.trim())}>Put on shift anyway</button><button type="button" className="btn btn--outline btn--xs" onClick={onCancel}>Cancel</button></div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ equipment */

const KIND_LABEL: Record<string, string> = { ute: "Ute", trailer: "Trailer", other: "Other" };

export function Equipment() {
  const s = useAsync(kits);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  async function run(fn: () => Promise<void>, done: string) {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      ok(done);
      setAdding(false);
      s.reload();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  const label = BRAND.equipment?.kitLabel ?? "Kit";
  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader eyebrow="Operations" title="Equipment" meta={`${label}s (utes, trailers) and what is in each. Put a ${label.toLowerCase()} on a shift in Jobs; the crew check it before and after.`} actions={!adding ? <button type="button" className="btn btn--primary btn--sm" onClick={() => setAdding(true)}><Icon name="plus" size={16} /> New {label.toLowerCase()}</button> : undefined} />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {adding && <KitForm busy={busy} onCancel={() => setAdding(false)} onSave={(k) => run(() => saveKit(k), `${k.name} added.`)} />}
      {s.loading && !s.data && <Skeleton columns={3} />}
      {s.error && <ErrorState message={s.error} onRetry={s.reload} />}
      {s.data && !s.data.length && !adding && <EmptyState icon="hardhat" title={`No ${label.toLowerCase()}s yet`}>Add each ute and trailer with its list of equipment.</EmptyState>}
      <div className="stack">{(s.data ?? []).map((k) => <KitCard key={k.id} kit={k} busy={busy} run={run} />)}</div>
    </section>
  );
}

function KitForm({ kit, busy, onSave, onCancel }: { kit?: Kit; busy: boolean; onSave: (k: { id?: string; name: string; kind: string; registration: string | null; notes: string | null; active: boolean }) => void; onCancel: () => void }) {
  const [f, setF] = useState({ name: kit?.name ?? "", kind: kit?.kind ?? "ute", registration: kit?.registration ?? "", notes: kit?.notes ?? "", active: kit?.active ?? true });
  return (
    <form className="card card__body form-grid" onSubmit={(e) => { e.preventDefault(); onSave({ id: kit?.id, name: f.name.trim(), kind: f.kind, registration: f.registration.trim() || null, notes: f.notes.trim() || null, active: f.active }); }}>
      <Field label="Name"><input className="input" required maxLength={120} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ute 1" /></Field>
      <Field label="Type"><select className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as Kit["kind"] })}>{Object.entries(KIND_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
      <Field label="Registration"><input className="input" maxLength={20} value={f.registration} onChange={(e) => setF({ ...f, registration: e.target.value })} /></Field>
      <Field label="Notes" wide><input className="input" maxLength={300} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
      {kit && <label className="check"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> In use</label>}
      <div className="form-actions field--wide"><button type="submit" className="btn btn--ink btn--sm" disabled={busy}>Save</button><button type="button" className="btn btn--outline btn--sm" onClick={onCancel}>Cancel</button></div>
    </form>
  );
}

function KitCard({ kit, busy, run }: { kit: Kit; busy: boolean; run: (fn: () => Promise<void>, done: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [item, setItem] = useState({ name: "", quantity: "1" });
  const items = kit.equipment_kit_items.filter((i) => i.active).sort((a, b) => a.sort_order - b.sort_order);
  return (
    <div className="card">
      <div className="card__head">
        <h2 className="card__title">{kit.name}<span className="sub">{KIND_LABEL[kit.kind]}{kit.registration ? `, ${kit.registration}` : ""}{kit.active ? "" : ", not in use"}</span></h2>
        {!editing && <button type="button" className="btn btn--outline btn--xs" onClick={() => setEditing(true)}>Edit</button>}
      </div>
      <div className="card__body stack">
        {editing && <KitForm kit={kit} busy={busy} onCancel={() => setEditing(false)} onSave={(k) => void run(() => saveKit(k), "Saved.").then(() => setEditing(false))} />}
        {items.length === 0 && <p className="small muted" style={{ margin: 0 }}>No items yet. The crew check each item before and after the shift.</p>}
        {items.length > 0 && (
          <ul className="list">
            {items.map((i) => (
              <li key={i.id} className="list__row"><span>{i.name}</span><span className="list__value">{i.quantity} <button type="button" className="linkbtn small" disabled={busy} onClick={() => void run(() => removeKitItem(i.id), `${i.name} removed.`)}>Remove</button></span></li>
            ))}
          </ul>
        )}
        <form className="form-grid" onSubmit={(e) => { e.preventDefault(); void run(() => addKitItem(kit.id, item.name.trim(), Math.max(1, Number(item.quantity) || 1), items.length), `${item.name} added.`).then(() => setItem({ name: "", quantity: "1" })); }}>
          <Field label="Item"><input className="input" required maxLength={120} value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} placeholder="e.g. Stop/slow bat" /></Field>
          <Field label="How many"><input className="input" type="number" min={1} max={999} value={item.quantity} onChange={(e) => setItem({ ...item, quantity: e.target.value })} /></Field>
          <div className="form-actions field--wide"><button type="submit" className="btn btn--outline btn--xs" disabled={busy}><Icon name="plus" size={14} /> Add item</button></div>
        </form>
      </div>
    </div>
  );
}

/** ShiftKits with its own data, for one shift row in Jobs. */
export function ShiftKitsCell({ bookingId }: { bookingId: string }) {
  const s = useAsync(async () => ({ all: await kits(), ...(await shiftKits([bookingId])) }), [bookingId]);
  if (!equipmentInUse() || !s.data) return null;
  return <ShiftKits bookingId={bookingId} allKits={s.data.all} rows={s.data.kits} checks={s.data.checks} onChanged={s.reload} />;
}

/** On a shift in Jobs: the kits on it, adding one, and the result of its checks. */
export function ShiftKits({ bookingId, allKits, rows, checks, onChanged }: { bookingId: string; allKits: Kit[]; rows: ShiftKitRow[]; checks: CheckRow[]; onChanged: () => void }) {
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const on = rows.filter((r) => r.booking_id === bookingId);
  const label = BRAND.equipment?.kitLabel ?? "Kit";
  const run = (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    fn().then(onChanged).catch((e) => setErr(e instanceof Error ? e.message : String(e))).finally(() => setBusy(false));
  };
  return (
    <div className="stack" style={{ gap: 6 }}>
      {on.map((r) => {
        const k = allKits.find((x) => x.id === r.kit_id);
        const mine = checks.filter((c) => c.booking_id === bookingId && c.kit_id === r.kit_id);
        return (
          <span key={r.id} className="small">
            <strong>{k?.name ?? label}</strong>{" "}
            {(["before", "after"] as const).map((ph) => {
              const c = mine.find((x) => x.phase === ph);
              return c ? (
                <span key={ph}>
                  <Chip tone={c.problems ? "primary" : "green"}>{ph === "before" ? "Before" : "After"}: {c.problems ? `${c.problems} problem${c.problems === 1 ? "" : "s"}` : "OK"}</Chip>{" "}
                  {c.items.filter((i) => i.status !== "present").map((i) => (
                    <span key={i.name} className="sub sub--primary">{i.name}: {i.status}{i.note ? `. ${i.note}` : ""} {i.photoKeys.map((key, n) => <button key={key} type="button" className="linkbtn" onClick={() => void openBlobInNewTab(() => checkPhoto(c.id, key))}>photo {n + 1}</button>)}</span>
                  ))}
                </span>
              ) : (
                <Chip key={ph} tone="outline">{ph === "before" ? "Before" : "After"}: not checked</Chip>
              );
            })}{" "}
            {!mine.length && <button type="button" className="linkbtn" disabled={busy} onClick={() => run(() => unassignKit(r.id))}>Take off</button>}
          </span>
        );
      })}
      <span className="addcrew">
        <select className="input" aria-label={`Add a ${label.toLowerCase()} to this shift`} value={pick} onChange={(e) => setPick(e.target.value)}>
          <option value="">Add {label.toLowerCase()}</option>
          {allKits.filter((k) => k.active && !on.some((r) => r.kit_id === k.id)).map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
        <button type="button" className="btn btn--outline btn--xs" disabled={!pick || busy} onClick={() => run(async () => { await assignKit(bookingId, pick); setPick(""); })}>Add</button>
      </span>
      {err && <span className="sub sub--primary">{err}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ the job pack */

export function PackKitChecks({ bookingId }: { bookingId: string }) {
  const s = useAsync(() => packKits(bookingId), [bookingId]);
  if (!equipmentInUse() || !s.data || !s.data.kits.length) return null;
  const { kits: list, checks, canCheck, kitLabel } = s.data;
  return (
    <div className="card">
      <div className="card__head"><h2 className="card__title">{kitLabel} checks</h2></div>
      <div className="card__body stack">
        {list.map((k) => (
          <div key={k.id} className="stack" style={{ gap: 8 }}>
            <p className="strong" style={{ margin: 0 }}>{k.name}{k.registration ? `, ${k.registration}` : ""}</p>
            {(["before", "after"] as const).map((ph) => {
              const done = checks.find((c) => c.kitId === k.id && c.phase === ph);
              return done ? (
                <p key={ph} className="small" style={{ margin: 0 }}>
                  <Chip tone={done.problems ? "primary" : "green"}>{ph === "before" ? "Before" : "After"}: {done.problems ? `${done.problems} problem${done.problems === 1 ? "" : "s"}` : "all there"}</Chip>{" "}
                  {done.checkedByName ?? "Checked"}, {fmtDate(done.checkedAt)}
                </p>
              ) : canCheck ? (
                <CheckForm key={ph} bookingId={bookingId} kit={k} phase={ph} onDone={s.reload} />
              ) : (
                <p key={ph} className="small muted" style={{ margin: 0 }}>{ph === "before" ? "Before" : "After"}: not checked yet.</p>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function CheckForm({ bookingId, kit, phase, onDone }: { bookingId: string; kit: { id: string; name: string; items: { id: string; name: string; quantity: number }[] }; phase: "before" | "after"; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<Record<string, { status: string; note: string }>>({});
  const [photos, setPhotos] = useState<Record<string, File[]>>({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const { notice, setNotice, fail, ok } = useNotice();
  const max = BRAND.equipment?.maxPhotosPerItem ?? 2;
  if (!open) return <button type="button" className="btn btn--ink btn--block" onClick={() => setOpen(true)}>{phase === "before" ? "Check before the shift" : "Check after the shift"}</button>;
  const set = (id: string, p: Partial<{ status: string; note: string }>) => setAnswers((a) => ({ ...a, [id]: { status: a[id]?.status ?? "", note: a[id]?.note ?? "", ...p } }));
  const allAnswered = kit.items.every((i) => answers[i.id]?.status);
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        setBusy(true);
        setNotice(null);
        submitCheck(bookingId, kit.id, phase, kit.items.map((i) => ({ itemId: i.id, status: answers[i.id]!.status, note: answers[i.id]!.note.trim() || null })), photos, notes)
          .then((r) => {
            ok(r.check.problems ? "Saved. The office has been told about the problem." : "Saved. Everything is there.");
            onDone();
          })
          .catch(fail)
          .finally(() => setBusy(false));
      }}
    >
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <p className="small strong" style={{ margin: 0 }}>{phase === "before" ? "Before the shift" : "After the shift"}: check each item</p>
      {kit.items.map((i) => (
        <fieldset key={i.id} className="stack" style={{ gap: 4, border: 0, padding: 0, margin: 0 }}>
          <legend className="small strong">{i.name}{i.quantity > 1 ? ` (${i.quantity})` : ""}</legend>
          <div className="checks">
            {(["present", "missing", "damaged"] as const).map((st) => (
              <label key={st} className="check"><input type="radio" name={`${phase}-${i.id}`} checked={answers[i.id]?.status === st} onChange={() => set(i.id, { status: st })} /> {st === "present" ? "There" : st === "missing" ? "Missing" : "Damaged"}</label>
            ))}
          </div>
          {answers[i.id]?.status && answers[i.id]!.status !== "present" && (
            <>
              <input className="input" required placeholder="What is wrong?" value={answers[i.id]!.note} onChange={(e) => set(i.id, { note: e.target.value })} />
              {max > 0 && <label className="btn btn--outline btn--xs" style={{ alignSelf: "flex-start" }}>{photos[i.id]?.length ? `${photos[i.id]!.length} photo${photos[i.id]!.length === 1 ? "" : "s"}` : "Add a photo"}<input className="sr-only" type="file" accept="image/*" capture="environment" multiple onChange={(e) => setPhotos((p) => ({ ...p, [i.id]: [...(e.target.files ?? [])].slice(0, max) }))} /></label>}
            </>
          )}
        </fieldset>
      ))}
      <Field label="Notes for the office"><textarea className="input" rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      <div className="form-actions"><button type="submit" className="btn btn--primary btn--block" disabled={busy || !allAnswered} aria-busy={busy}>Save the check</button><button type="button" className="btn btn--outline btn--block" onClick={() => setOpen(false)}>Close</button></div>
    </form>
  );
}
